import dom, { Fragment } from "../../../../dist/jsx-render/dom";
import JSXComponent from "../../../../dist/jsx-render/JSXComponent";
import { TreeNodeModel } from "../Models/TreeNodeModel";
import { TextComponent } from "../../TextComponent";
import { Constants } from "../../../Common/constants";
import { ITreeNodeInternalComponent } from "../Models/Interfaces/ITreeNodeInternalComponent";
import { ITreeNodeModel, ISubGroupModel } from "../Models/Interfaces/ITreeNodeModel";
import { TreeView } from "../TreeView";
import { app } from "../../../Frame/App";
import { CheckedState } from "../Models/enums/CheckedState";
import { LanguageString } from "../../../Model/LanguageString";

export type TreeNodeComponentProps = {
    model: ITreeNodeModel;
    level: number;
    allowDragDrop?: boolean;
    allowSelect?: boolean;
    checkboxCheckedEvent?: (node: ITreeNodeModel, event: Event) => void;
    dropTargetClass?: string;
    dragDropBusinessModelAction?: (draggedModel: any, droppedOnModel: any) => void;
    childrenCollapsed?: boolean;
    searchTerm?: string;
    insertOnTop?: boolean;

    /** insert drag element on same level and not as child */
    dropOnSameLevel: boolean;

    /** used to create tree model when drag source is another control */
    createTreeNodeModelOnDrop: (elementId: string) => ITreeNodeModel;

    treeViewParent: TreeView;

    internalComponentType?: ITreeNodeInternalComponent;
    groupHeaderComponentType?: any;

    isNew?: boolean;
    isFirst?: boolean;

    /** An external event handler to override the internal delete confirmation. */
    externalConfirmDeleteAction: (node: TreeNodeComponent) => boolean;
    afterAddAction?: () => void;
    afterDeleteAction?: () => void;

    /** An external event handler, that executes when a node is clicked */
    nodeClickAction?: (node: TreeNodeComponent, event: MouseEvent) => void;

    /** props of the internal node component */
    internalProps: any;

    /** props of the group header component */
    groupHeaderInternalProps: any;

    /** To deal with huge data sets, activate this function. Childnodes of the next level are then created when a node is expanded */
    loadChildrenOnExpand?: boolean;

    preventDragOnNodesWithChildren?: boolean;

    insertBeforeNode?: TreeNodeComponent;

    preventFullWidth?: boolean;
}


export class TreeNodeComponent extends JSXComponent {
    constructor(props: TreeNodeComponentProps) { super(props); }
    props: TreeNodeComponentProps;

    clientId: string;
    childNodes: TreeNodeComponent[] = [];
    parentNode: TreeNodeComponent;

    private childrenLoaded: boolean = false;
    private useGrouping;

    private checkboxElement: HTMLInputElement;

    //childrenCollapsed: boolean = false;

    private toggleCollapsed = (event: MouseEvent) => {
        if (this.props.childrenCollapsed) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    collapse = () => {
        let childNodes = this.element.querySelectorAll(".tree-node-wrapper.level-" + (this.props.level + 1));
        let groupedChildNodes = this.element.querySelectorAll(".grouped-branches.level-" + (this.props.level + 1));

        childNodes.forEach(n => n.classList.add(Constants.cssClass_hidden));
        groupedChildNodes.forEach(n => n.classList.add(Constants.cssClass_hidden));

        if (this.hasExpander()) {
            this.expander.classList.remove("fa-caret-down");
            this.expander.classList.add("fa-caret-right");
        }
        this.props.childrenCollapsed = true;
    }

    expand = () => {
        if (this.props.loadChildrenOnExpand && !this.childrenLoaded) {
            for (let model of this.props.model.children.filter(c => !c.businessModel.isDeleted)) {
                model.visible = true;
                if (this.childNodes.filter(n => n.props.model == model).length == 0) {
                    if (this.useGrouping) {
                        this.addGroupedChildNode(model);
                    } else {
                        this.addChildNodeByNodeModel(model);
                    }
                }
            }
            this.childrenLoaded = true;
        }

        let childNodes = this.element.querySelectorAll(".tree-node-wrapper.level-" + (this.props.level + 1));
        let groupedChildNodes = this.element.querySelectorAll(".grouped-branches.level-" + (this.props.level + 1));

        childNodes.forEach(n => n.classList.remove(Constants.cssClass_hidden));
        groupedChildNodes.forEach(n => n.classList.remove(Constants.cssClass_hidden));

        if (this.hasExpander()) {
            this.expander.classList.remove("fa-caret-right");
            this.expander.classList.add("fa-caret-down");
        }
        this.props.childrenCollapsed = false;
    }

    hasExpander = (): boolean => {
        if (this.element.querySelector(".tree-node-expander")) {
            return true;
        }
        return false;
    }

    /** Add an expander to this Node, if it gets Children at runtime */
    addExpander() {
        if (!this.hasExpander()) {
            var nodeElement = this.element.querySelector(".tree-node");
            var treeNodeRow = this.element.firstChild;
            this.expander = this.getExpander();

            if (this.props.allowSelect) {
                let selectElement = this.element.querySelector(".select");
                treeNodeRow.insertBefore(this.expander, selectElement);
            } else {
                treeNodeRow.insertBefore(this.expander, nodeElement);
            }
        }
    }

    removeExpander = () => {
        if (this.hasExpander() && !this.useGrouping) {
            this.expander.remove();
        }
    }

    addChildNode = (newBusinessModel?: any) => {
        let childNode = this.createTreeNodeModel(newBusinessModel);

        this.props.model.children.push(childNode);

        this.addExpander();
        let firstNode = this.element.querySelector(".tree-node-wrapper");
        if (firstNode && this.props.insertOnTop) {
            this.element.insertBefore(this.getChildNode(childNode, true), firstNode);
        } else {
            this.element.appendChild(this.getChildNode(childNode, true));
        }

        if (this.props.afterAddAction) {
            this.props.afterAddAction();
        }
    }

    private addChildNodeByNodeModel = (model: TreeNodeModel) => {

        this.addExpander();
        let firstNode = this.element.querySelector(".tree-node-wrapper");
        if (firstNode && this.props.insertOnTop) {
            this.element.insertBefore(this.getChildNode(model, true), firstNode);
        } else {
            this.element.appendChild(this.getChildNode(model, true));
        }

        if (this.props.afterAddAction) {
            this.props.afterAddAction();
        }
    }

    addGroupedChildNode = (nodeModel: ITreeNodeModel) => {
        let groupElement = this.element
            .querySelector(`.grouped-branches.level-${(this.props.level + 1)} [data-group-id='${nodeModel.parentSubGroup.id}']`);

        let firstNode = groupElement.querySelector(".tree-node-wrapper");
        if (firstNode && this.props.insertOnTop) {
            groupElement.insertBefore(this.getChildNode(nodeModel, true), firstNode);
        } else {
            groupElement.appendChild(this.getChildNode(nodeModel, true));
        }

        if (this.props.afterAddAction) {
            this.props.afterAddAction();
        }
    }

    private createTreeNodeModel(newBusinessModel: any) {
        let node = new TreeNodeModel();
        node.businessModel = newBusinessModel;
        node.children = [];
        if (this.props.model.displayProperty) {
            node.displayProperty = this.props.model.displayProperty;
        }
        return node;
    }

    /**
     * This method is passed to the internal component, which uses it as event handler
     * for its delete button. That way, the confirmation can be handled by the tree node,
     * because delete confirmation is always needed, independent from the concrete 
     * implementation of the model oder internal component.
     * @param externalDeleteHandler
     */
    private deleteNode = (externalDeleteHandler?: (model: any) => void) => {
        let doDelete: boolean = false;
        if (this.props.externalConfirmDeleteAction) {
            doDelete = this.props.externalConfirmDeleteAction(this);
        } else {
            doDelete = this.internalConfirmDelete();
        }

        if (doDelete) {
            if (externalDeleteHandler) {
                externalDeleteHandler(this.props.model.businessModel);
            }

            app.destroyDomElements(this.element);
            if (this.parentNode) {
                this.parentNode.childNodes.splice(this.parentNode.childNodes.indexOf(this), 1);

                if (this.parentNode.childNodes.length == 0) {
                    this.parentNode.removeExpander();
                }
            }

            if (this.props.afterDeleteAction) {
                this.props.afterDeleteAction();
            }
        }
    }

    private internalConfirmDelete(): boolean {
        if (this.props.model.children.length > 0) {
            return confirm(Resources.TreeView.diaConfirmDeleteWithChildren);
        } else {
            return confirm(Resources.TreeView.diaConfirmDelete);
        }
    }

    private handleClick = (event: MouseEvent) => {
        if (this.props.nodeClickAction) {
            this.props.nodeClickAction(this, event);
        }
    }

    getChildNode = (model: TreeNodeModel, isNew?: boolean, isFirst?: boolean, useSpacer?: boolean): HTMLElement => {
        let props: TreeNodeComponentProps = {
            model: model,
            level: this.props.level + 1,
            treeViewParent: this.props.treeViewParent,
            internalComponentType: this.props.internalComponentType,
            internalProps: this.props.internalProps,
            childrenCollapsed: isNew ? true : this.props.childrenCollapsed,
            groupHeaderComponentType: this.props.groupHeaderComponentType,
            groupHeaderInternalProps: this.props.groupHeaderInternalProps,
            externalConfirmDeleteAction: this.props.externalConfirmDeleteAction,
            isNew: isNew,
            isFirst: isFirst,
            nodeClickAction: this.props.nodeClickAction,
            afterAddAction: this.props.afterAddAction,
            afterDeleteAction: this.props.afterDeleteAction,
            allowDragDrop: this.props.allowDragDrop,
            dropTargetClass: this.props.dropTargetClass,
            dragDropBusinessModelAction: this.props.dragDropBusinessModelAction,
            searchTerm: this.props.searchTerm,
            insertOnTop: this.props.insertOnTop,
            allowSelect: this.props.allowSelect,
            dropOnSameLevel: this.props.dropOnSameLevel,
            createTreeNodeModelOnDrop: this.props.createTreeNodeModelOnDrop,
            checkboxCheckedEvent: this.props.checkboxCheckedEvent,
            loadChildrenOnExpand: this.props.loadChildrenOnExpand,
            preventDragOnNodesWithChildren: this.props.preventDragOnNodesWithChildren
        };
        let node = new TreeNodeComponent({ ...props });
        node.parentNode = this;
        node.props.model.parent = this.props.model;
        if (this.props.treeViewParent.props.levelProperty && this.props.model.businessModel)
            node.props.model.businessModel[this.props.treeViewParent.props.levelProperty] = this.props.level + 1;

        this.childNodes.push(node);

        if (!this.props.model.children.includes(node.props.model)) {
            this.props.model.children.push(node.props.model);
        }
        if (!this.props.treeViewParent.flatModel.includes(model)) {
            this.props.treeViewParent.flatModel.push(model);
        }
        return node.render();
    }

    expander: HTMLElement;
    getExpander(): HTMLElement {

        let caretClass = this.props.childrenCollapsed ? "fa-caret-right" : "fa-caret-down";
        var expander: HTMLElement = <div className={`icon fas pointer ${caretClass} tree-node-expander`} />;

        expander.addEventListener('click', this.toggleCollapsed);
        return expander;
    }

    conditionalRenderExpander() {
        if ((this.props.model.children.filter(c => !c.businessModel.isDeleted === true).length > 0)
            || (this.props.model.availableSubGroups && this.props.model.availableSubGroups.length > 0)) {
            this.expander = this.getExpander();
            return this.expander;
        }
        return [];
    }



    resetLevelClass = (newLevel: number) => {
        let elementsLevelClass = this.element.querySelectorAll(".level-" + this.props.level);
        let elements = Array.prototype.slice.call(elementsLevelClass) as HTMLElement[];
        elements.push(this.element);

        elements.forEach(e => e.classList.remove("level-" + this.props.level));
        this.props.level = newLevel;
        elements.forEach(e => e.classList.add("level-" + this.props.level));

        this.childNodes.forEach(n => n.resetLevelClass(newLevel + 1));
    }

    removeChild(node: TreeNodeComponent) {
        this.childNodes.splice(this.childNodes.indexOf(node), 1);
        this.props.model.children.splice(this.props.model.children.indexOf(node.props.model), 1);
    }

    //addChild(node: TreeNodeComponent) {
    //    this.childNodes.push(node);
    //    this.props.model.children.push(node.props.model);
    //    node.parentNode = this;
    //}

    element: HTMLElement;
    getElement(): HTMLElement {
        this.useGrouping = this.props.model.availableSubGroups && this.props.model.availableSubGroups.length > 0;

        let InternalComponent: any;
        if (this.props.internalComponentType) {
            InternalComponent = this.props.internalComponentType;
        }
        let childrenList = this.props.model.children;
        let checkedChildrenItems = 0;

        for (var i = 0; i < childrenList.length; i++) {
            if (childrenList[i].selected == CheckedState.Checked) {
                checkedChildrenItems += 1;
            }
        }
        let guid: string = app.createGuid();
        //get list of childrenElements

        let nodeElement = <div className={"tree-node-wrapper level-" + this.props.level.toString()}>
            <div className={"tree-node-row level-" + this.props.level.toString()}>

                {this.conditionalRenderExpander()}

                {this.props.allowSelect
                    ? <div className="select">
                        {this.getCheckbox(checkedChildrenItems)}
                    </div>
                    : []}

                <div className={"tree-node level-" + this.props.level.toString()}>
                    {this.props.internalComponentType
                        ? <InternalComponent
                            model={this.props.model.businessModel}
                            addChildNodeAction={this.addChildNode}
                            deleteNodeAction={this.deleteNode}
                            isNew={this.props.isNew}
                            isFirst={this.props.isFirst}
                            nodeClickAction={this.handleClick}
                            searchTerm={this.props.searchTerm}
                            node={this}
                            {...this.props.internalProps}
                        />
                        : <span className="text-node">
                            {this.getDisplayText()}
                        </span>
                    }
                </div>
            </div>
            {this.useGrouping
                ? this.getGroupedChildren(true)
                : this.getChildren()}
        </div>;

        // Not yet implemented for categories with multiple selection
        //if (this.props.model.colorProperty && this.props.model.businessModel[this.props.model.colorProperty]) {
        //    let nodeRow = nodeElement.querySelector(".tree-node-row.level-" + this.props.level.toString()) as HTMLDivElement;
        //    if (nodeRow) {
        //        nodeRow.classList.add("color-border");
        //        nodeRow.style.backgroundColor = this.props.model.businessModel[this.props.model.colorProperty];
        //    }
        //}

        if (this.props.preventFullWidth) {
            (nodeElement as HTMLElement).style.width = "initial";
        }

        return nodeElement;
    }

    getDisplayText = (): string => {
        return this.props.model.getDisplayName();

        //if (this.props.model.businessModel[this.props.model.displayProperty]
        //    && (this.props.model.businessModel[this.props.model.displayProperty]["valueCs"]
        //    || this.props.model.businessModel[this.props.model.displayProperty]["valueDe"]
        //    || this.props.model.businessModel[this.props.model.displayProperty]["valueEn"]
        //    || this.props.model.businessModel[this.props.model.displayProperty]["valueFr"]
        //    || this.props.model.businessModel[this.props.model.displayProperty]["valueKo"]
        //    || this.props.model.businessModel[this.props.model.displayProperty]["valueNl"]
        //    || this.props.model.businessModel[this.props.model.displayProperty]["valueRu"]
        //    || this.props.model.businessModel[this.props.model.displayProperty]["valueSv"])
        //) {
        //    return app.getNameInLanguage(this.props.model.businessModel[this.props.model.displayProperty]);
        //} else {
        //    return this.props.model.businessModel[this.props.model.displayProperty];
        //}
    }

    getCheckbox = (checkedChildrenItems: number) => {
        let className: string;
        //if (this.props.model.selected === CheckedState.Indeterminate
        //    || (checkedChildrenItems > 0 && checkedChildrenItems < this.props.model.children.length)) {
        //    className = "node-checkbox blue";
        //}
        //else {
            className = "node-checkbox blue";
        //}

        if (this.props.model.selected === CheckedState.Checked) {
            this.checkboxElement = <input type="checkbox" className={className} checked="true" />
        } else if (this.props.model.selected === CheckedState.Indeterminate) { 
            this.checkboxElement = <input type="checkbox" className={className} />
            this.checkboxElement.indeterminate = true;
        } else {
            this.checkboxElement = <input type="checkbox" className={className} />
        }

        this.checkboxElement.disabled = this.props.treeViewParent.props.isDisabled ?? false;

        return this.checkboxElement;
    }

    getChildren = (): HTMLElement[] => {
        return (this.props.model.children.length > 0 && this.props.level < app.maxTreeViewDisplayLevels)
            ? this.props.model.children
                .filter(n => n.visible === true)
                .map((c, index) => {
                    return (index == 0
                        ? this.getChildNode(c, undefined, true)
                        : this.getChildNode(c))
                })
            : []
    }

    getGroupHeader = (groupModel: ISubGroupModel): HTMLElement => {
        let GroupHeaderComponent: any;
        if (this.props.groupHeaderComponentType) {
            GroupHeaderComponent = this.props.groupHeaderComponentType;

            return <GroupHeaderComponent
                model={groupModel.businessModel}
                parentModel={this.props.model}
                externalAddAction={this.addGroupedChildNode}
                {...this.props.groupHeaderInternalProps} />;
        } else {
            return <div className="group-name">{groupModel.name}</div>;
        }
    }

    getGroupedChildren = (hidden: boolean): HTMLElement[] => {
        if (this.props.level < app.maxTreeViewDisplayLevels) {
            let groupedModels = this.props.model.getGroupedChildren();

            return <div className={`grouped-branches level-${this.props.level + 1} ${Constants.cssClass_hidden}`}>
                {this.props.model.availableSubGroups.map((subgroup, index) => {
                    return (<div className={"group group-" + index + " level-" + (this.props.level + 1)} data-group-id={subgroup.id.toString()}>

                        {this.getGroupHeader(subgroup)}

                        {(groupedModels[subgroup.id.toString()] ?? [])
                            .filter(n => n.visible === true)
                            .map(c => {
                                return index == 0
                                    ? this.getChildNode(c, undefined, true, false)
                                    : this.getChildNode(c, undefined, undefined, false)
                            })}

                    </div>)
                })}
            </div>;
        } else {
            return [];
        }
    }

    // Drag & Drop
    private onDragStart = (event: DragEvent) => {
        event.cancelBubble = true;
        event.stopPropagation();
        event.dataTransfer.setData("elementId", this.element.id);
    }

    private allowDrop = (event: DragEvent) => {
        let draggedOverElement = event.target as HTMLElement;

        let dropTarget: string;
        if (this.props.dropTargetClass) {
            dropTarget = this.props.dropTargetClass;
            if (draggedOverElement.classList.contains(dropTarget)) {
                event.preventDefault();
                event.stopPropagation();
                draggedOverElement.classList.add(Constants.cssClass_dragHighlighted);
            }
            else if (draggedOverElement.parentElement.classList.contains(dropTarget)) {
                event.preventDefault();
                event.stopPropagation();
                draggedOverElement.parentElement.classList.add(Constants.cssClass_dragHighlighted);
            }
        } else {
            // TODO Test with tree view without internal component and choose an appropriate class as drop target.
        }
    }

    private onDragLeave = (event: DragEvent) => {
        let draggedOverElement = event.target as HTMLElement;
        if (draggedOverElement.classList.contains(Constants.cssClass_dragHighlighted)) {
            draggedOverElement.classList.remove(Constants.cssClass_dragHighlighted);
        }
        else if (draggedOverElement.parentElement.classList.contains(Constants.cssClass_dragHighlighted)) {
            draggedOverElement.parentElement.classList.remove(Constants.cssClass_dragHighlighted);
        }
    }

    private drop = (event: DragEvent) => {
        event.stopPropagation();
        event.preventDefault();

        let draggedId = event.dataTransfer.getData("elementId");
        if (document.getElementById(draggedId).contains(this.element)) {
            // Do nothing, you can't drop a node on one of its children.
        } else {
            let draggedNode = this.props.treeViewParent.nodes.find(n => n.clientId == draggedId);

            if (draggedNode) {

                // remove node from current parent or rootNodes if node does not have a parent
                if (draggedNode.parentNode) {
                    draggedNode.parentNode.removeChild(draggedNode);

                    if (draggedNode.parentNode.childNodes.length == 0) {
                        draggedNode.parentNode.removeExpander();
                    }
                } else {
                    let previousIndexOfDraggedNode = this.props.treeViewParent.nodes.indexOf(draggedNode);
                    this.props.treeViewParent.nodes.splice(previousIndexOfDraggedNode, 1);
                }

                if (this.props.dropOnSameLevel) {
                    this.element.parentElement.insertBefore(draggedNode.element, this.element);

                    // if we are dropping on the same level, find the index of the dropped on model and attach the dragged model before this position
                    if (this.parentNode) {
                        let indexOfThisNode = this.parentNode.childNodes.indexOf(this);
                        this.parentNode.childNodes.splice(indexOfThisNode, 0, draggedNode);
                    } else {
                        // this is a root node
                        let indexOfThisNode = this.props.treeViewParent.nodes.indexOf(this);
                        this.props.treeViewParent.nodes.splice(indexOfThisNode, 0, draggedNode);
                    }
                }
                else {
                    this.appendDraggedNode(draggedNode);

                    //this.addChild(draggedNode);

                    if (this.props.dragDropBusinessModelAction) {
                        this.props.dragDropBusinessModelAction(draggedNode.props.model.businessModel, this.props.model.businessModel);
                    }

                    if (!this.hasExpander()) {
                        this.addExpander();
                    }

                    this.expand();
                }
            }
            else {
                // new node from other tree
                if (this.props.createTreeNodeModelOnDrop) {
                    const newNodeModel = this.props.createTreeNodeModelOnDrop(draggedId);
                    if (newNodeModel) {
                        if (this.parentNode) {
                            throw "Insert on parent node ist not yet implemented";
                        }
                        else {
                            if (this.props.treeViewParent.props.levelProperty && newNodeModel.businessModel)
                                newNodeModel.businessModel[this.props.treeViewParent.props.levelProperty] = newNodeModel.parent.level + 1;

                            this.props.treeViewParent.insertRootNode(newNodeModel, this);
                        }
                    }
                }
            }
        }

        // Remove all highlights that may have survied. Drag operations aren't 
        // 100% predictable, so the mouseleave event not always fires.
        document.querySelectorAll(`.${Constants.cssClass_dragHighlighted}`)
            .forEach(e => (e as HTMLElement).classList.remove(Constants.cssClass_dragHighlighted));
    }

    appendDraggedNode = (draggedNode: TreeNodeComponent) => {
        let firstNodeChild = this.element.querySelector(".tree-node-wrapper");
        if (firstNodeChild) {
            this.element.insertBefore(draggedNode.element, firstNodeChild);

            // also add the dragged model at the start of the children array
            this.childNodes.splice(0, 0, draggedNode);
        } else {
            this.element.appendChild(draggedNode.element);
            this.childNodes.push(draggedNode);
        }
        draggedNode.resetLevelClass(this.props.level + 1);

        if (this.props.treeViewParent.props.levelProperty && draggedNode.props.model.businessModel)
            draggedNode.props.model.businessModel[this.props.treeViewParent.props.levelProperty] = this.props.level + 1;

        draggedNode.parentNode = this;
    }
    // Drag & Drop end


    private handleSelect = (event: Event) => {
        if ((event.target as HTMLInputElement).checked) {
            this.select(false);
        } else {
            this.unselect(false);
        }

        if (this.props.checkboxCheckedEvent) {
            this.props.checkboxCheckedEvent(this.props.model, event);
        }
    }

    select = (updateCheckbox: boolean, changeParent: boolean = true) => {
        this.props.model.selected = CheckedState.Checked;

        // only add to selected ids, if the node doesn't have children, or any selected node should be added
        this.addToSelectedIds();
        this.removeIndeterminateStyle();

        if (updateCheckbox) {
            let checkboxElement = this.element.querySelector(".select input") as HTMLInputElement;
            checkboxElement.checked = true;
        }

        if (!this.props.treeViewParent.props.selectNodesIndependently) {
            // set checked states of child node components and child models seperately.
            // When loadChildrenOnExpand is true, not all child nodes might have been rendered yet.
            this.applyStateToChildModelsRecursive(this.props.model, CheckedState.Checked);

            // Then, select the exisiting child nodes
            this.childNodes.forEach(n =>
                n.select(true, false)
            );
        }
        if (changeParent && this.parentNode) {
            this.parentNode.checkboxApplyChildrenState();
        }
    }

    unselect = (updateCheckbox: boolean, changeParent: boolean = true) => {
        this.props.model.selected = CheckedState.Unchecked;

        this.removeFromSelectedIds();
        this.removeIndeterminateStyle();

        if (updateCheckbox) {
            //let checkboxElement = this.element.querySelector(".select input") as HTMLInputElement;
            this.checkboxElement.checked = false;
        }
        if (!this.props.treeViewParent.props.selectNodesIndependently) {
            // set checked states of child node components and child models seperately.
            // When loadChildrenOnExpand is true, not all child nodes might have been rendered yet.
            this.applyStateToChildModelsRecursive(this.props.model, CheckedState.Unchecked);

            // Then, unselect the exisiting child nodes
            this.childNodes.forEach(n =>
                n.unselect(true, false)
            );
        }
        if (changeParent && this.parentNode) {
            this.parentNode.checkboxApplyChildrenState();
        }
    }

    private applyStateToChildModelsRecursive = (parentModel: ITreeNodeModel, state: CheckedState) => {
        if (parentModel.children) {
            for (let model of parentModel.children) {
                model.selected = state;

                if (state == CheckedState.Checked) {
                    this.addModelToSelectedIds(model);
                } else if (state == CheckedState.Unchecked) {
                    this.removeModelFromSelectedIds(model);
                }

                this.applyStateToChildModelsRecursive(model, state);
            }
        }
    }

    checkboxApplyChildrenState = () => {
        if (this.props.model.children && this.props.model.children.length > 0 && !this.props.treeViewParent.props.selectNodesIndependently) {
            //let checkboxElement = this.element.querySelector(".select input") as HTMLInputElement;

            if (this.props.model.children.every(n => n.selected === CheckedState.Checked)) {
                this.props.model.selected = CheckedState.Checked;
                // TODO checkbox set state in its own method
                this.checkboxElement.checked = true;
                this.removeIndeterminateStyle();
                this.addToSelectedIds();

            } else if (this.props.model.children.every(n => n.selected === CheckedState.Unchecked)) {
                this.props.model.selected = CheckedState.Unchecked
                this.checkboxElement.checked = false;
                this.removeIndeterminateStyle();
                this.removeFromSelectedIds();
            } else {
                // Children have mixed states
                this.props.model.selected = CheckedState.Indeterminate;
                this.checkboxElement.checked = false;
                this.addIndeterminateStyle();
                this.removeFromSelectedIds();
            }
        }
        if (this.parentNode) {
            this.parentNode.checkboxApplyChildrenState();
        }
    }

    addIndeterminateStyle = () => {
        if (this.checkboxElement) {
            this.checkboxElement.indeterminate = true;
            //this.checkboxElement.classList.remove("fa-square");
            //this.checkboxElement.classList.remove("far");
            //this.checkboxElement.classList.add("fad");
            //this.checkboxElement.classList.add("fa-square-full");
        }
    }

    removeIndeterminateStyle = () => {
        if (this.checkboxElement) {
            this.checkboxElement.indeterminate = false;
            //this.checkboxElement.classList.remove("fad");
            //this.checkboxElement.classList.remove("fa-square-full");
            //this.checkboxElement.classList.add("fa-square");
            //this.checkboxElement.classList.add("far");
        }
    }

    /** Add the id of the current tree item to the selected ids, if certain conditions apply: It's either the end
     * of a branch or parent items are allowed to be added to the selected ids, too (Setting: includeAncestorsInSelected).
     * */
    private addToSelectedIds() {
        let selectedIdsElement = this.props.treeViewParent.element.querySelector(".selected-ids") as HTMLInputElement;
        if ((this.props.treeViewParent.props.includeAncestorsInSelected || this.childNodes.length == 0 || this.props.treeViewParent.props.selectNodesIndependently)
            && !this.props.treeViewParent.props.selectedIds.includes(this.props.model.businessModel.id)) {

            this.props.treeViewParent.props.selectedIds.push(this.props.model.businessModel.id);
            if (selectedIdsElement) {
                selectedIdsElement.value = this.props.treeViewParent.props.selectedIds.join(";");
            }
        }
    }

    private addModelToSelectedIds(model: ITreeNodeModel) {
        let selectedIdsElement = this.props.treeViewParent.element.querySelector(".selected-ids") as HTMLInputElement;
        if ((this.props.treeViewParent.props.includeAncestorsInSelected || !model.children?.length || this.props.treeViewParent.props.selectNodesIndependently)
            && !this.props.treeViewParent.props.selectedIds.includes(model.businessModel.id)) {

            this.props.treeViewParent.props.selectedIds.push(model.businessModel.id);
            if (selectedIdsElement) {
                selectedIdsElement.value = this.props.treeViewParent.props.selectedIds.join(";");
            }
        }
    }

    /** Removes the id of the current tree item from the selected ids. The conditions in the addToSelectedIds function are not applied here,
     * if the element is in the selected ids, it will be removed.
    * */
    private removeFromSelectedIds() {
        if (this.props.treeViewParent.props.selectedIds.includes(this.props.model.businessModel.id)) {
            this.props.treeViewParent.props.selectedIds.splice(
                this.props.treeViewParent.props.selectedIds.indexOf(this.props.model.businessModel.id),
                1);
        }
        let selectedIdsElement = this.props.treeViewParent.element.querySelector(".selected-ids") as HTMLInputElement;
        if (selectedIdsElement) {
            selectedIdsElement.value = this.props.treeViewParent.props.selectedIds.join(";");
        }
    }

    private removeModelFromSelectedIds(model: ITreeNodeModel) {
        if (this.props.treeViewParent.props.selectedIds.includes(model.businessModel.id)) {
            this.props.treeViewParent.props.selectedIds.splice(
                this.props.treeViewParent.props.selectedIds.indexOf(model.businessModel.id),
                1);
        }
        let selectedIdsElement = this.props.treeViewParent.element.querySelector(".selected-ids") as HTMLInputElement;
        if (selectedIdsElement) {
            selectedIdsElement.value = this.props.treeViewParent.props.selectedIds.join(";");
        }
    }

    render() {
        if (!(this.props.childrenCollapsed === true || this.props.childrenCollapsed === false)) {
            // set default value
            this.props.childrenCollapsed = true;
        }

        if (this.props.insertBeforeNode) {
            let index = this.props.treeViewParent.nodes.indexOf(this.props.insertBeforeNode);
            if (index > -1) {
                this.props.treeViewParent.nodes.splice(index, 0, this);
            }
        }

        if (this.props.treeViewParent.nodes.indexOf(this) == -1) {
            this.props.treeViewParent.nodes.push(this);
        }

        this.element = this.getElement();

        if (this.props.childrenCollapsed && this.props.level > 0 && !this.props.isNew) {
            this.element.classList.add(Constants.cssClass_hidden);
        }

        if (this.props.allowDragDrop === true) {
            this.clientId = app.createGuid();
            this.element.id = this.clientId;
            if (!this.props.preventDragOnNodesWithChildren || this.element.childNodes.length == 1) {
                this.element.setAttribute("draggable", "true");

                this.element.addEventListener('dragstart', this.onDragStart);
            }
            this.element.addEventListener('dragleave', this.onDragLeave);
            this.element.addEventListener('dragover', this.allowDrop);
            this.element.addEventListener('drop', this.drop);
        }

        if (this.props.allowSelect) {
            let checkboxElement = this.element.querySelector(".select input") as HTMLInputElement;
            checkboxElement.addEventListener('change', this.handleSelect);
        }



        return this.element;
    }
}